/**
 * Utility for making authenticated API calls with App Bridge session tokens
 * This version safely checks for App Bridge availability
 */

import { shopifyAppBridge } from '../App';

/**
 * Check if App Bridge is available
 * @returns {boolean} True if App Bridge is initialized
 */
export function isAppBridgeAvailable() {
  return shopifyAppBridge !== null || 
         (typeof window !== 'undefined' && window.shopify !== undefined);
}

/**
 * Hook to get App Bridge instance and make authenticated API calls
 * Only works in embedded app context (where App Bridge is initialized)
 * @returns {Object} Object with app instance and authenticatedFetch function
 */
export function useAuthenticatedFetch() {
  const authenticatedFetch = async (url, options = {}) => {
    // Get App Bridge instance - check exported instance first, then window.shopify
    const appBridge = shopifyAppBridge || (typeof window !== 'undefined' ? window.shopify : null);
    
    if (!appBridge) {
      throw new Error('App Bridge not initialized. This route requires embedded app context.');
    }

    try {
      // Use appBridge.idToken() method to get session token
      const sessionToken = await appBridge.idToken();
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`,
        ...options.headers,
      };

      const response = await fetch(url, {
        ...options,
        headers,
      });

      // If unauthorized, might need to redirect to OAuth
      if (response.status === 401) {
        const data = await response.json().catch(() => ({}));
        if (data.message?.includes('OAuth') || data.message?.includes('installation')) {
          // Redirect to OAuth
          const shop = new URLSearchParams(globalThis.window.location.search).get('shop');
          if (shop) {
            globalThis.window.location.href = `/login?shop=${shop}`;
            return;
          }
        }
      }

      return response;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Authenticated fetch error:', error);
      }
      throw error;
    }
  };

  // Return App Bridge instance for components that need it
  const appBridge = shopifyAppBridge || (typeof window !== 'undefined' ? window.shopify : null);
  return { app: appBridge, authenticatedFetch };
}

/**
 * Check if we're in an embedded context
 * @returns {boolean}
 */
export function isEmbedded() {
  return typeof window !== 'undefined' && window.location !== window.parent.location;
}

